import { useEditFactorsDealMutation } from '@/app/api';
import { getFetchEror } from '@/app/helpers';
import { useNotification } from '@/shared/contexts/NotificationContext';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import {
  checkContractFieldsFilled,
  checkDebtRegistryFieldsFilled,
  WatchedFormValues,
} from './check-fields-filled';

interface Params {
  debouncedWatchedValues: WatchedFormValues;
  watchedFactorTerm: number | null;
  isApprove: boolean;
  isContractAndRegistrySent: boolean | undefined;
  dealId: number;
  blockEditAfterCreation: React.MutableRefObject<boolean>;
}

export const useAutoSaveDealConditions = ({
  debouncedWatchedValues,
  watchedFactorTerm,
  isApprove,
  isContractAndRegistrySent,
  dealId,
  blockEditAfterCreation,
}: Params) => {
  const { showNotification } = useNotification();
  const [editDealConditions] = useEditFactorsDealMutation();
  const lastSavedBodyRef = useRef<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (blockEditAfterCreation.current) return;
    if (!isApprove && !isContractAndRegistrySent) return;

    const [
      recourseType,
      factorDealAmount,
      factorDiscount,
      factorPaymentAmount,
      factorPaymentDate,
      comissionCalculationTypeId,
      isTripartiteContract,
      factoringContractNumber,
      factoringContractDate,
      registerOfDebtsNumber,
      registerOfDebtsDate,
    ] = debouncedWatchedValues;

    const isContractFilled = checkContractFieldsFilled(debouncedWatchedValues);
    const isDebtRegistryFilled = checkDebtRegistryFieldsFilled(
      debouncedWatchedValues
    );

    if (!isContractFilled && !isDebtRegistryFilled) return;

    const body: any = {
      id: dealId,
      factorDealAmount: Number(String(factorDealAmount).replace(/\s/g, '')),
      factorDiscount: Number(String(factorDiscount).replace(/\s/g, '')),
      factorPaymentAmount: Number(
        String(factorPaymentAmount).replace(/\s/g, '')
      ),
      factorPaymentDate: dayjs(factorPaymentDate).format('DD/MM/YYYY'),
      recourseTypeId: recourseType,
      comissionCalculationTypeId: comissionCalculationTypeId?.id,
      isTripartiteContract: isTripartiteContract || null,
      ...(watchedFactorTerm != null && { factorTerm: watchedFactorTerm }),
    };

    if (isContractFilled && factoringContractNumber && factoringContractDate) {
      body.factoringContractNumber = factoringContractNumber;
      body.factoringContractDate = dayjs(factoringContractDate).format(
        'DD/MM/YYYY'
      );
    }

    if (isDebtRegistryFilled && registerOfDebtsNumber && registerOfDebtsDate) {
      body.registerOfDebtsNumber = registerOfDebtsNumber;
      body.registerOfDebtsDate =
        dayjs(registerOfDebtsDate).format('DD/MM/YYYY');
    }

    const bodyKey = JSON.stringify(body);
    if (lastSavedBodyRef.current === bodyKey) return;

    const save = async () => {
      try {
        setIsSaving(true);
        await editDealConditions(body).unwrap();
        lastSavedBodyRef.current = bodyKey;
      } catch (error) {
        showNotification(getFetchEror(error), 'error');
      } finally {
        setIsSaving(false);
      }
    };

    save();
  }, [
    debouncedWatchedValues,
    watchedFactorTerm,
    isApprove,
    isContractAndRegistrySent,
    dealId,
  ]);

  return { isSaving };
};
